import { computed, ComputedRef, inject, InjectionKey, reactive, Ref, ref, watch } from "vue";
import { SkywardException } from "../../../../skyward.base.error";
import { Dictionary } from "../../../../types/util/dictionary";
import * as MathUtil from "../../../core/math";
import { registerStore } from "../../core/store";
import { Data, State } from "../../core/store/types";
import { BrowseCell, BrowseRow, BrowseColumn } from "../types";
import { browseStoreFeatures } from "./features";
import { BrowseSetup, BrowseStoreState, BrowseStoreFeatures, RowID } from "./types";

export const browseStoreKey: InjectionKey<BrowseStoreState<any, any>> = Symbol("browseStore");
export const browseStore = registerStore<BrowseStoreFeatures, ReturnType<typeof setupStore>, ReturnType<typeof setupState>>(browseStoreKey, browseStoreFeatures, setupStore, setupState);

function setupStore()
{
    let selectedBrowseId = ref();

    return {
        get selectedBrowseId() { return selectedBrowseId.value; },
        set selectedBrowseId(value)
        {
            if (value)
            {
                const browseState = browseStore.states.get(value);

                if (!browseState)
                {
                    throw new Error(`Browse with id=${value} can't be selected because it doesn't exist in the states collection.`);
                }

                if (!browseState.features.settings.canBeSelected)
                {
                    throw new Error(`Browse with id=${value} can't be selected because the setting CanBeSelected=false}.`);
                }
            }

            selectedBrowseId.value = value;
        },
        getBrowseState,
        injectBrowseState,
    };

    function getBrowseState<TFeatureTypes, TDataType extends object = Dictionary<string, any>, TNonPageable extends Dictionary<string, any> = {}>(browseId: string)
    {
        return browseStore.getStateAsync<TFeatureTypes, TDataType, TNonPageable>(browseId);
    }

    function injectBrowseState<TFeatureTypes, TDataType extends object = Dictionary<string, any>>(): BrowseStoreState<TFeatureTypes, TDataType>
    {
        return inject(browseStoreKey) as BrowseStoreState<TFeatureTypes, TDataType>;
    }
}

export function getDisplayRows<T>(data: Data<T>, computedDisplayColumns: ComputedRef<BrowseColumn[]>, identifierField: keyof T): ComputedRef<BrowseRow[]>
{
    return computed(() =>
    {
        const displayRows: BrowseRow[] = [];
        if (data.length === 0)
        {
            return displayRows;
        }
        const displayColumns = computedDisplayColumns.value;
        const dottedPaths = displayColumns.map(x => x.fieldName.split(/\./g));

        for (let i = 0; i < data.length; i++)
        {
            const singleRowData: Dictionary<string, any> = data.values[i];

            const cells: Array<BrowseCell> = [];

            for (let i = 0; i < displayColumns.length; i += 1)
            {
                const browseCell: BrowseCell = Object.create(null);
                browseCell.column = displayColumns[i];
                browseCell.value = lookupDottedPath(singleRowData, dottedPaths[i]) ?? "";

                cells.push(browseCell);
            }

            const browseRow: BrowseRow =
            {
                cells: cells,
                rowData: singleRowData,
                rowID: (singleRowData[identifierField as string] || `defaultRowId${i}`)
            };
            displayRows.push(browseRow);
        }

        return displayRows;

        function lookupDottedPath(row: any, path: string[])
        {
            return path.reduce((val, key) => val?.[key], row);
        }
    });
}

function setupState(browseID: string, storeState: State<BrowseStoreFeatures, {}, {}>): BrowseSetup
{
    const rowRefs: BrowseSetup["rowRefs"] = reactive({});
    let selectedRowID: Ref<RowID> = ref('');
    let inFocusRowID: Ref<RowID> = ref('');

    const displayColumns = computed(() => storeState.features.browseView.columns.filter(c => c.forDisplay));

    const addRowRef = (rowID: keyof BrowseSetup["rowRefs"], element: Element) => { rowRefs[rowID] = element };

    const displayRows = getDisplayRows(storeState.data, displayColumns, storeState.features.dataSource.identifierField);

    const rowsByID = computed(() =>
    {
        const rowsByID: Dictionary<string, BrowseRow> = Object.create(null);
        if (displayRows.value.length > 0)
        {
            for (const row of displayRows.value)
            {
                rowsByID[row.rowID] = row;
            }
        }

        return rowsByID;
    });

    const state =
    {
        get browseID() { return browseID; },
        get isSelected() { return browseStore.selectedBrowseId === browseID; },
        set isSelected(value)
        {
            if (value)
            {
                browseStore.selectedBrowseId = browseID;
            }
            else if (browseStore.selectedBrowseId === browseID)
            {
                browseStore.selectedBrowseId = null;
            }
        },
        get displayColumns() { return displayColumns.value; },
        computedDisplayColumns: displayColumns,
        get displayRows() { return displayRows.value; },
        computedDisplayRows: displayRows,
        get rowsByID() { return rowsByID.value; },
        get rowCount() { return storeState.data.length; },
        get selectedRowID() { return selectedRowID.value; },
        get selectedRowRef() { return rowRefs[selectedRowID.value]; },
        get inFocusRowRef() { return rowRefs[inFocusRowID.value]; },
        get inFocusRowID() { return inFocusRowID.value; },
        set inFocusRowID(value) { inFocusRowID.value = value; },
        rowRefs,
        addRowRef,
        selectRow,
        moveSelectedRow
    };

    watch(selectedRowID, (newValue:any) =>
    {
        inFocusRowID.value = newValue;
    });

    watch(inFocusRowID, () =>
    {
        if (state.inFocusRowRef)
        {
            $(state.inFocusRowRef as Element).scrollintoview();
        }
    });

    return state;

    function moveSelectedRow(deltaDirection: number): void
    {
        const indexOfSelectedRow = storeState.data.getIndexByIdentifyingField(selectedRowID.value);

        const newIndex = MathUtil.constrain(indexOfSelectedRow + deltaDirection, 0, storeState.data.length - 1);
        const newId = storeState.data.identifyingFieldValues[newIndex];

        selectRow(newId, true);
    }

    function selectRow(rowID: RowID, selectBrowse?: boolean): void
    {
        if (rowID === undefined)
        {
            window.skyward.error.reportException(new SkywardException("Undefined RowID"));
            return;
        }

        if (selectedRowID.value !== rowID)
        {
            selectedRowID.value = rowID;
        }
        if (selectBrowse !== undefined && state.isSelected !== selectBrowse)
        {
            state.isSelected = selectBrowse;
        }
    }
}
